/**
 * Bottom Tab Navigation Component
 *
 * Native-feel tab bar for mobile navigation with 4 core sections:
 * - Home (Dashboard + Quick Actions)
 * - Create (Blog Generation)
 * - History (Previous Requests)
 * - Services (Agency Marketing)
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Home, PenSquare, Clock, Briefcase } from 'lucide-react';

export type MobileTabId = 'home' | 'create' | 'history' | 'services';

interface TabConfig {
  id: MobileTabId;
  name: string;
  icon: React.ElementType;
}

const MOBILE_TABS: TabConfig[] = [
  { id: 'home', name: 'Home', icon: Home },
  { id: 'create', name: 'Create', icon: PenSquare },
  { id: 'history', name: 'History', icon: Clock },
  { id: 'services', name: 'Services', icon: Briefcase },
];

interface BottomTabNavProps {
  activeTab: MobileTabId;
  onTabChange: (tab: MobileTabId) => void;
}

export const BottomTabNav: React.FC<BottomTabNavProps> = ({
  activeTab,
  onTabChange,
}) => {
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around h-16 px-2">
        {MOBILE_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="flex flex-col items-center justify-center flex-1 h-full relative touch-target"
              aria-label={tab.name}
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-purple-600 rounded-b-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}

              <motion.div
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className={`flex flex-col items-center gap-1 ${
                  isActive ? 'text-purple-600' : 'text-gray-500'
                }`}
              >
                <Icon
                  className={`w-6 h-6 transition-colors ${
                    isActive ? 'text-purple-600' : 'text-gray-400'
                  }`}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                <span
                  className={`text-xs font-medium transition-colors ${
                    isActive ? 'text-purple-600' : 'text-gray-500'
                  }`}
                >
                  {tab.name}
                </span>
              </motion.div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTabNav;
